import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { WebUserDataConstants } from '@ddt-web/domain';
import { SharedQueryParamKey, UrlUtils } from '@ddt-web/url';
import { Store } from '@ngrx/store';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';

import { AllAvailableFeatureFlags } from '../configs/all-flags.config';
import { FeatureFlagConstants } from '../constants/feature-flag.contants';
import { FeatureFlagConfig, InternalApplicationStateStoreProxyService } from '../feature-flag-config.interface';

@Injectable({
	providedIn: 'root'
})
export class FeatureFlagLoaderService {
	public static instance: FeatureFlagLoaderService; // this is mainly for the decorator

	private apiGatewayPath: string;
	private featureFlags: string[];
	private featureFlags$: Subject<string[]> = new BehaviorSubject(undefined);
	private isProdDmz: boolean;
	private applicationStateStoreProxyService: any;

	constructor(
		@Optional() private http?: HttpClient,
		@Optional() private servicePathProvider?: ServicePathProviderService,
		@Optional() @Inject(InternalApplicationStateStoreProxyService) private featureFlagConfig?: FeatureFlagConfig,
		@Optional() private store?: Store
	) {
		FeatureFlagLoaderService.instance = this;

		this.apiGatewayPath = this.servicePathProvider.getServicePath('api-gateway');
		if (this.featureFlagConfig?.applicationStateStoreProxyService) {
			this.applicationStateStoreProxyService = new (this.featureFlagConfig.applicationStateStoreProxyService)(this.store);
		}
	}

	// load config and read selected variation
	public init(): Promise<string[]> {
		this.isProdDmz = UrlUtils.isProdDmz();

		return this.retrieveConfig().pipe(
			first(),
			map(featureFlags => {
				this.featureFlags = featureFlags;
				if (!!this.applicationStateStoreProxyService) {
					this.applicationStateStoreProxyService.updateFeatureFlags(featureFlags);
				} else {
					console.warn('Feature Flag warning: application state store is not registered, feature flag will not work in PDF');
				}
				this.updateFlagsFromLocalStorage();
				this.featureFlags$.next(this.featureFlags);
				return this.featureFlags;
			})
		).toPromise();
	}


	/**
	 * The hasBeenInitialised function checks whether the flags we need have been loaded yet from the API.
	 * It should be used in places where we have no guarantee that the API call would have been completed such as RouteGuards and constructors.
	 * @returns Observable of true if the flags have been retrieved from the API, of false otherwise - continuing to emit as the flags are updated
	 */
	public hasBeenInitialised(): Observable<boolean> {
		return this.featureFlags$.asObservable().pipe(map(value => {
			return !!value;
		}));
	}

	/**
	 * @deprecated Use InjectFeatureFlag instead
	 */
	public isFeatureEnabled(flag: AllAvailableFeatureFlags): boolean {
		const isEnabledLocally = !this.isProdDmz && this.getLocalFeatureFlag(flag);
		return (!!this.featureFlags && this.featureFlags.includes(flag)) || isEnabledLocally;
	}

	public retrieveConfig(): Observable<string[]> {
		// local query string
		const parsedUrl = new URL(window.location.href);
		const localViewId = parsedUrl.searchParams.get(SharedQueryParamKey.FEATURE_FLAG);

		// get tool name from url
		const urlParts = window.location.pathname.split('/');
		const toolsPartIndex = urlParts.findIndex(part => part === 'tools' || part === FeatureFlagConstants.ModelsExperience.urlPart);
		let toolNamePart = toolsPartIndex > 0 ? get(urlParts, toolsPartIndex + 1, 'default') : 'default';

		// Temp changes for models experience, where models-experience should be used as viewId when application name is model-portfolios
		toolNamePart = toolNamePart === FeatureFlagConstants.ModelsExperience.appName ? FeatureFlagConstants.ModelsExperience.viewId : toolNamePart;

		const isAcDashboard = urlParts.includes('financial-professionals') && urlParts.includes('dashboard');
		const toolName = isAcDashboard ? 'dashboard' : toolNamePart;

		// finalize viewId
		const viewId = !this.isProdDmz && localViewId && localViewId !== 'true' ? localViewId : toolName;

		return !!this.http ?
			this.http.get(this.apiGatewayPath + WebUserDataConstants.FEATURE_FLAG_CONFIG + `?viewId=${viewId}`).pipe(
				map(yamlResponse => get(yamlResponse, 'enabledFeatures', [])),
				map((config: { key: string }[]) => {
					return Object.values(config).map(flag => flag.key);
				}),
				catchError(error => {
					console.error('Feature Flag config loading error, All feature flagged will be disabled:', error);
					return of([]);
				})
			) :
			of([]);
	}

	private getLocalFeatureFlag(flag: string): boolean {
		return !!localStorage.getItem(flag);
	}

	private updateFlagsFromLocalStorage(): void {
		if (!this.isProdDmz) {
			const localStorageFlags = Object.entries(localStorage).map(item => item[0]).filter(item => item.startsWith('FEATURE_FLAG'));
			if (!isEmpty(localStorageFlags) && !!this.applicationStateStoreProxyService) {
				this.applicationStateStoreProxyService.updateFeatureFlags(localStorageFlags);
			}
		}
	}
}
